import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, Outlet, createBrowserRouter, useNavigate, useRouteError } from 'react-router';
import { Shell } from './components/Shell';
import { sessionEvents } from './lib/api';
import { SessionContext, useMe } from './lib/queries';
import { Biblioteca } from './views/Biblioteca';
import { Config } from './views/Config';
import { Dna } from './views/Dna';
import { Inicio } from './views/Inicio';
import { Login } from './views/Login';
import { Peca } from './views/Peca';
import { Pulse } from './views/Pulse';
import { Strategy } from './views/Strategy';
import { Studio } from './views/Studio';
import { ToolRoute } from './views/ToolView';

/** Sem sessão, vai para o login. Com sessão, entra na moldura. */
function RequireSession() {
  const me = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  useEffect(() => {
    const onExpired = () => { qc.clear(); nav('/login', { replace: true }); };
    sessionEvents.addEventListener('expired', onExpired);
    return () => sessionEvents.removeEventListener('expired', onExpired);
  }, [qc, nav]);

  if (me.isPending) return <div className="boot" aria-busy="true"><span className="mark">ADS</span></div>;
  if (me.isError || !me.data) return <Navigate to="/login" replace />;
  const projectId = me.data.projects[0]?.id;
  if (!projectId) return <div className="boot"><p>Nenhum projeto nesta organização.</p></div>;
  return (
    <SessionContext.Provider value={{ me: me.data, projectId }}>
      <Outlet />
    </SessionContext.Provider>
  );
}

function RouteError() {
  const e = useRouteError() as Error | undefined;
  return (
    <div className="boot">
      <span className="mark">ADS</span>
      <p>Algo saiu do lugar.{e?.message ? ` (${e.message})` : ''}</p>
      <a className="btn-ghost" href="/inicio">Voltar ao início</a>
    </div>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  {
    element: <RequireSession />,
    errorElement: <RouteError />,
    children: [{
      element: <Shell />,
      children: [
        { index: true, element: <Navigate to="/inicio" replace /> },
        { path: 'inicio', element: <Inicio /> },
        { path: 'strategy', element: <Strategy /> },
        { path: 'strategy/:tool', element: <ToolRoute /> },
        { path: 'dna', element: <Dna /> },
        { path: 'studio', element: <Studio /> },
        { path: 'studio/:tool', element: <Studio /> },
        { path: 'pulse', element: <Pulse /> },
        { path: 'biblioteca', element: <Biblioteca /> },
        { path: 'pecas/:id', element: <Peca /> },
        { path: 'configuracoes', element: <Config /> },
        { path: '*', element: <Navigate to="/inicio" replace /> },
      ],
    }],
  },
]);
